import { v2ProductCheckoutEnabled } from '@/components/DmpV2/features';
import { axios } from '@/utils/axios';

export type DmpPlanCode = 'free' | 'individual' | 'sme_firm' | 'barrister' | 'law_firm';
export type DmpProductKind = 'plan' | 'standalone_package' | 'top_up';
export interface DmpRight {
  limit_type: 'limited' | 'unlimited' | 'unavailable' | 'conditional' | 'planned' | 'available_add_on';
  quantity?: number;
  unit?: string;
}
export interface DmpPlan {
  code: DmpPlanCode;
  name: string;
  price: number;
  purchasable: boolean;
  billing_period_months: number;
  entitlements: Record<string, DmpRight>;
}
export interface DmpService {
  code: string;
  name: string;
  kind: 'standalone_package' | 'top_up';
  feature_key: string;
  quantity: number;
  unit: string;
  price: number;
  amount_minor: number;
  purchasable: boolean;
  availability: string;
  purchase_block_reason?: string | null;
  expiry_policy: string;
  settlement_policy: string;
}
export interface DmpCatalogV2 {
  identity_enabled?: boolean;
  catalog_version: string;
  status: string;
  purchasable: boolean;
  enforcement_enabled: boolean;
  currency: string;
  plans: DmpPlan[];
  services: DmpService[];
}
export interface DmpQuoteV2 {
  quote_id: string;
  product_code: string;
  catalog_version: string;
  full_amount_minor: number;
  amount_minor: number;
  credit_minor: number;
  currency: string;
  operation: 'new_purchase' | 'renewal' | 'scheduled_change' | 'plan_upgrade' | 'legacy_upgrade' | 'deferred_plan_purchase';
  activation_policy?: 'after_qualification_and_paid_periods';
  qualification_required?: 'company' | 'barrister' | null;
  qualification_ready?: boolean;
  activation_after?: string | null;
  expires_at: string;
  top_up_plan_end: string | null;
  renew_after: string | null;
  renew_until?: string | null;
  source_grant: { id: number; plan_code: string; valid_from: string; valid_until: string } | null;
}
export interface DmpLot {
  id: number;
  product_code: string;
  source_type: string;
  limit_type?: 'limited' | 'unlimited';
  remaining: number;
  reserved: number;
  valid_until: string;
}
export interface DmpMeter {
  reconciliation_required?: boolean;
  feature_key: string;
  unit: string;
  entitled?: boolean;
  unlimited: boolean;
  available: number;
  reserved: number;
  debt: number;
  lots: DmpLot[];
}
export interface DmpMembership {
  seat_limit: number | null;
  selection_required: boolean;
  allowed_user_ids?: number[];
  actor_allowed?: boolean;
  context_hash?: string;
}
export interface DmpSubscriptionV2 {
  legacy_external_quota_managed?: boolean;
  pending_purchases?: DmpPendingPurchase[];
  identity_period_reviews?: { plan_code: string; valid_from: string; valid_until: string; status: string }[];
  billing_owner_id: number;
  is_main_account: boolean;
  current_plan: {
    code: string;
    name: string;
    entitlements?: Record<string, DmpRight>;
    valid_from: string | null;
    valid_until: string | null;
  };
  future_periods: { plan_code: string; valid_from: string; valid_until: string }[];
  usage: Record<string, DmpMeter>;
  storage_capacity_bytes: number;
  membership: DmpMembership;
  can_purchase: boolean;
  checkout_ready: boolean;
  service_readiness?: {
    external_features?: Record<string, boolean>;
    external_services: boolean;
    voice_transcription: boolean;
    document_measurement: boolean;
  };
  members?: { id: number; acc_activated: boolean }[];
  own_usage: { id: number; feature_key: string; unit: string; state: string; consumed: number; started_at: string }[];
  transactions?: {
    id: number;
    status: string;
    checkout_status?: string;
    paid_purchase_status?: string | null;
    payment_status?: string | null;
    product_code: string | null;
    plan_code: string;
    product_kind: string | null;
    amount_minor: number;
    currency: string;
    created_at: string;
  }[];
}
export interface DmpPendingPurchase {
  id: number;
  order_id: number;
  product_code: string;
  status: 'awaiting_qualification' | 'scheduled' | 'manual';
  paid_at: string;
  qualified_at: string | null;
  activation_after: string | null;
  reason: string | null;
}
export interface BarristerProfile {
  english_name: string;
  business_name: string;
  jurisdiction: string;
  business_address: string;
  qualification_evidence: string;
  chinese_name?: string;
  chambers?: string;
  website?: string;
  practice_areas?: string[];
  public_fields: string[];
}
export interface BarristerState {
  identity_managed?: boolean;
  identity_flow_required?: boolean;
  status: string;
  version: number;
  approved_profile: BarristerProfile | null;
  general_profile: Partial<BarristerProfile>;
  latest_submission: { id: number; status: string; data: BarristerProfile; review_reason: string | null } | null;
}
export interface DmpOrderV2 {
  order_id: number;
  product_code: string;
  product_kind: DmpProductKind;
  checkout_status: string;
  fulfilled: boolean;
}
export interface DmpApplication {
  id: number;
  uuid: string;
  product_code: string;
  feature_key: string;
  state: string;
  subject: { name: string; kind: string; confirmed: boolean };
  accepted_at: string | null;
  issued_from: string | null;
  issued_until: string | null;
}
type Api<T> = { success: boolean; data: T; message?: string };
export { v2UiEnabled } from '@/components/DmpV2/features';
// 公開資料不依賴會員登入，避免過期登入狀態妨礙重新選購。
export const getCatalogV2 = () => axios.get<Api<DmpCatalogV2>>('/api/catalog/v2', { skipAuthRefresh: true });
export const getSubscriptionV2 = () => axios.get<Api<DmpSubscriptionV2>>('/api/account/v2/subscription');
export const getBarristerProfile = () => axios.get<Api<BarristerState>>('/api/account/v2/barrister-profile');
export const submitBarristerProfile = (profile: BarristerProfile, reason: string) =>
  axios.post<Api<{ request_id: number; status: string }>>('/api/account/v2/barrister-profile', { profile, reason });
export const updateBarristerGeneral = (
  profile: Pick<BarristerProfile, 'website' | 'practice_areas' | 'public_fields'>
) => axios.patch<Api<{ updated: boolean }>>('/api/account/v2/barrister-profile/general', profile);
export const quoteProductV2 = (product_code: string) =>
  axios.post<Api<DmpQuoteV2>>('/api/payment/dmp/v2/quote', { product_code });
export const checkoutProductV2 = (product_code: string, quote_id: string, key: string, upgrade_acknowledged = false) =>
  !v2ProductCheckoutEnabled(product_code) ? Promise.reject(new Error('dmp_v2_checkout_disabled')) : axios.post<Api<{ orderId: number; sessionId: string; url: string | null; checkoutStatus: string }>>(
    '/api/payment/dmp/v2/checkout',
    { product_code, quote_id, upgrade_acknowledged },
    { headers: { 'Idempotency-Key': key } }
  );
export const getOrderV2 = (id: number) => axios.get<Api<DmpOrderV2>>('/api/payment/dmp/v2/orders/' + id);
export const selectMembersV2 = (selected_user_ids: number[], context_hash: string) =>
  axios.put<Api<DmpMembership>>('/api/account/v2/subscription/members', { selected_user_ids, context_hash });
export const getApplicationsV2 = () =>
  axios.get<Api<{ items: DmpApplication[] }>>('/api/account/v2/service-applications');
export const createApplicationV2 = (product_code: string, subject: DmpApplication['subject'], key: string) =>
  axios.post<Api<{ id: number; state: string }>>(
    '/api/account/v2/service-applications',
    { product_code, subject },
    { headers: { 'Idempotency-Key': key } }
  );

export const resumeOrderV2 = (id: number) =>
  axios.post<Api<{ orderId: number; url: string | null; checkoutStatus: string }>>(
    '/api/payment/dmp/v2/orders/' + id + '/resume'
  );

export const recoverApplicationV2 = (key: string) =>
  axios.get<{ data: { id: number; uuid: string; state: string } }>('/api/account/v2/service-applications/recovery', {
    headers: { 'Idempotency-Key': key },
  });

export interface PublicBarrister {
  id: number;
  profession: 'barrister';
  profile: Partial<
    Pick<
      BarristerProfile,
      'english_name' | 'chinese_name' | 'business_name' | 'chambers' | 'jurisdiction' | 'website' | 'practice_areas'
    >
  >;
}
export const getPublicBarristersV2 = (after?: number) =>
  axios.get<Api<{ items: PublicBarrister[]; next_cursor: number | null }>>('/api/directory/v2/barristers', {
    skipAuthRefresh: true,
    params: { after, limit: 25 },
  });
